/**
 * RESPONSIBILITY: the rank an event carries at a given moment.
 *
 * An event states its importance as dated windows rather than as one number, so the
 * answer depends on when you ask. Every list, filter and search asks this one function,
 * because an order worked out in two places is two orders.
 *
 * IT IS A RANK AND NOT A SCORE, matching `position`, which it extends and does not
 * replace: 1 is the top of the page, 2 is below it, and 0 means "no opinion — sort me by
 * date with everything else". Higher-is-better would have read the same field two ways
 * the moment a window ran out, which is the kind of bug that only shows up in November.
 *
 * With no window covering the moment, `position` is the answer. That is not a fallback
 * bolted on: windows are the same question asked of the calendar instead of of a person.
 */

interface Window { from?: string; until?: string; priority?: number }
interface Ranked { position?: number; priorities?: Window[] }

/**
 * Inclusive of both ends: a window "from the 5th until the 5th" covers the 5th.
 *
 * AN ABSENT DATE IS UNBOUNDED ON THAT SIDE, and a window with neither date always
 * applies — it is simply a priority. Requiring both was the strict reading and the
 * useless one: a half-filled window silently did nothing at all, which is the worst
 * thing a misconfiguration can do, because there is nothing on the page to notice.
 */
const covers = (w: Window, day: string): boolean =>
  (!w.from || w.from <= day) && (!w.until || day <= w.until);

/**
 * The local day as `YYYY-MM-DD`, which is the form the date control stores. Comparing
 * those as strings is exact; parsing them into Dates first introduces a timezone that
 * nobody typed.
 */
export function today(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/**
 * The strongest window covering today, else `position`.
 *
 * Strongest and not first: overlapping windows are something an editor will do by
 * accident, and the answer that surprises least is the more important of the two. 0 is
 * not a rank, so it never wins a comparison against one.
 */
export function rankNow(e: Ranked, day: string = today()): number {
  const ranks = (e.priorities ?? [])
    .filter((w) => covers(w, day))
    .map((w) => w.priority ?? 0)
    .filter((n) => n > 0);

  return ranks.length ? Math.min(...ranks) : (e.position ?? 0);
}
